import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "../Reveal";
import { api } from "../../lib/api";

export const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...new Set(articles.map((a) => a.category))], [articles]);
  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <section id="learn" className="py-20 md:py-28 lg:py-36" data-testid="articles-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>03 · Learn</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-serif font-semibold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.12]" data-testid="articles-heading">
                Short reads, written for real life.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="flex flex-wrap gap-2" data-testid="article-category-tabs">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`article-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors duration-300 border ${
                        active === c ? "bg-ink text-cream border-ink" : "bg-white text-stone-600 border-linen hover:border-stone-400"}`}>
                {c}
              </button>
            ))}
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filtered.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <Link to={`/articles/${a.id}`} data-testid={`article-card-${a.id}`}
                    className="group flex flex-col sm:flex-row bg-white rounded-2xl border border-linen overflow-hidden h-full transition-shadow duration-300 hover:shadow-xl hover:shadow-stone-900/10">
                {a.image_url && (
                  <div className="sm:w-44 lg:w-52 shrink-0 h-44 sm:h-auto overflow-hidden">
                    <img src={a.image_url} alt="" loading="lazy"
                         className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.18em]">
                    <span className="text-ember">{a.category}</span>
                    <span className="text-stone-400 flex items-center gap-1"><Clock size={12} /> {a.read_time}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-ink leading-snug group-hover:text-ember transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-3">{a.excerpt}</p>
                  <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
                    Read it <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
